import {
  Box,
  Button,
  Tabs,
  Tab,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import React, { useState, useMemo, useEffect } from "react";
import SearchBar from "../../components/SearchBar";
import AddIcon from "@mui/icons-material/Add";
import SwitchToggle from "../../components/SwitchToggle";
import AddTypeFlavor from "../../Modals/addTypeFlavor";
import AddTypeTopping from "../../Modals/addTypeTopping";
import {
  useGetYogurtFlavorListQuery,
  useDeleteYogurtFlavorMutation,
  useUpdateYogurtFlavorMutation,
  useGetYogurtToppingListQuery,
  useDeleteYogurtToppingMutation,
  useUpdateYogurtToppingMutation,
  useGetYogurtTypeListQuery,
} from "../../store/api/commonAPI";
import { PricingMethod } from "../../constants/enums";
import Loader from "../../constants/Loader";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../Modals/deleteConfirmation";
import { showToast, showError } from "../../constants/toasts";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const YogurtTypeDetail = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;

  const { id } = useParams();

  // Fetch yogurt type details to get pricing method
  const { data: yogurtTypeData } = useGetYogurtTypeListQuery({
    page: 1,
    limit: 1000, // Get all to find the specific one
  });

  // Get pricing method for the current yogurt type
  const pricingMethod = useMemo(() => {
    if (yogurtTypeData?.data?.yogurt && Array.isArray(yogurtTypeData.data.yogurt)) {
      const yogurtType = yogurtTypeData.data.yogurt.find(
        (yogurt: any) => (yogurt._id || yogurt.id) === id
      );
      return yogurtType?.pricing || PricingMethod.PER_100G;
    }
    return PricingMethod.PER_100G;
  }, [yogurtTypeData, id]);

  // Reset to first page when search changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // Fetch yogurt flavors from API
  const { data, isLoading, error, refetch } = useGetYogurtFlavorListQuery({
    page,
    limit,
    search: searchTerm,
    id: id, // Pass yogurt type ID as query param to filter flavors
  });

  // Delete yogurt flavor mutation
  const [deleteYogurtFlavor, { isLoading: isDeleting }] =
    useDeleteYogurtFlavorMutation();

  // Update yogurt flavor mutation (for status toggle)
  const [updateYogurtFlavor, { isLoading: isUpdatingStatus }] =
    useUpdateYogurtFlavorMutation();

  // Store raw API data for edit
  const rawFlavorData = useMemo(() => {
    if (data?.data?.yogurtFlavors && Array.isArray(data.data.yogurtFlavors)) {
      return data.data.yogurtFlavors;
    }
    return [];
  }, [data]);

  // Separate state for toppings tab
  const [searchTermTopping, setSearchTermTopping] = useState("");
  const [pageTopping, setPageTopping] = useState(1);
  const limitTopping = 10;

  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  // Reset to first page when search changes for toppings
  useEffect(() => {
    if (value === 1) {
      setPageTopping(1);
    }
  }, [searchTermTopping, value]);

  // Fetch yogurt toppings from API
  const {
    data: toppingsData,
    isLoading: isLoadingToppings,
    error: errorToppings,
    refetch: refetchToppings,
  } = useGetYogurtToppingListQuery({
    page: pageTopping,
    limit: limitTopping,
    search: searchTermTopping,
    id: id, // Pass yogurt type ID as query param to filter toppings
  });

  // Store raw API data for edit toppings
  const rawToppingData = useMemo(() => {
    if (
      toppingsData?.data?.yogurtToppings &&
      Array.isArray(toppingsData.data.yogurtToppings)
    ) {
      return toppingsData.data.yogurtToppings;
    }
    return [];
  }, [toppingsData]);

  const [openModal, setOpenModal] = useState(false);
  const [editFlavor, setEditFlavor] = useState<any | null>(null);

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditFlavor(null);
    refetch();
  };

  // Handle edit button click
  const handleEditClick = (row: { id: string; name: string }) => {
    const rawItem = rawFlavorData.find(
      (item: any) => (item._id || item.id) === row.id
    );
    if (rawItem) {
      setEditFlavor(rawItem);
      setOpenModal(true);
    }
  };

  // Handle status toggle (isActive)
  const handleStatusToggle = async (
    flavorId: string,
    flavorName: string,
    currentStatus: boolean,
    currentFeatured: boolean
  ) => {
    const newStatus = !currentStatus;
    try {
      const payload = {
        id: flavorId,
        isActive: newStatus,
        featured: currentFeatured,
      };

      console.log("📤 Updating yogurt flavor status with payload:", payload);

      const response = await updateYogurtFlavor(payload).unwrap();

      console.log("✅ Update yogurt flavor status response:", response);

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt flavor "${flavorName}" has been ${
          newStatus ? "activated" : "deactivated"
        } successfully`;
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating yogurt flavor status:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${
          newStatus ? "activate" : "deactivate"
        } yogurt flavor "${flavorName}". Please try again.`;

      showError(errorMessage);
      refetch(); // Revert UI state if update failed
    }
  };

  // Handle featured toggle
  const handleFeaturedToggle = async (
    flavorId: string,
    flavorName: string,
    currentStatus: boolean,
    currentFeatured: boolean
  ) => {
    const newFeatured = !currentFeatured;
    try {
      const payload = {
        id: flavorId,
        isActive: currentStatus,
        featured: newFeatured,
      };

      console.log(
        "📤 Updating yogurt flavor featured status with payload:",
        payload
      );

      const response = await updateYogurtFlavor(payload).unwrap();

      console.log(
        "✅ Update yogurt flavor featured status response:",
        response
      );

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt flavor "${flavorName}" has been ${
          newFeatured ? "marked as featured" : "removed from featured"
        } successfully`;
      showToast(successMessage);
      refetch();
    } catch (error: any) {
      console.error("Error updating yogurt flavor featured status:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${
          newFeatured ? "mark as featured" : "remove from featured"
        } yogurt flavor "${flavorName}". Please try again.`;

      showError(errorMessage);
      refetch(); // Revert UI state if update failed
    }
  };

  const [openModal1, setOpenModal1] = useState(false);
  const [editTopping, setEditTopping] = useState<any | null>(null);

  const handleCloseModal1 = () => {
    setOpenModal1(false);
    setEditTopping(null);
    refetchToppings();
  };

  // Handle edit button click for toppings
  const handleEditClickTopping = (row: { id: string; name: string }) => {
    const rawItem = rawToppingData.find(
      (item: any) => (item._id || item.id) === row.id
    );
    if (rawItem) {
      setEditTopping(rawItem);
      setOpenModal1(true);
    }
  };

  // Delete yogurt topping mutation
  const [deleteYogurtTopping, { isLoading: isDeletingTopping }] =
    useDeleteYogurtToppingMutation();

  // Update yogurt topping mutation (for status toggle)
  const [updateYogurtTopping, { isLoading: isUpdatingToppingStatus }] =
    useUpdateYogurtToppingMutation();

  // Delete confirmation modal state for toppings
  const [openDeleteModalTopping, setOpenDeleteModalTopping] = useState(false);
  const [toppingToDelete, setToppingToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Handle delete button click for toppings - opens confirmation modal
  const handleDeleteClickTopping = (id: string, name: string) => {
    setToppingToDelete({ id, name });
    setOpenDeleteModalTopping(true);
  };

  // Handle delete confirmation for toppings - actually deletes the topping
  const handleConfirmDeleteTopping = async () => {
    if (!toppingToDelete) return;

    try {
      const response = await deleteYogurtTopping({
        id: toppingToDelete.id,
      }).unwrap();

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt topping "${toppingToDelete.name}" has been deleted successfully`;
      showToast(successMessage);

      // Refetch the list after successful deletion
      refetchToppings();
      // Close modal and reset state
      setOpenDeleteModalTopping(false);
      setToppingToDelete(null);
    } catch (error: any) {
      console.error("Error deleting yogurt topping:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to delete yogurt topping "${toppingToDelete.name}". Please try again.`;

      showError(errorMessage);
    }
  };

  // Handle delete modal close for toppings
  const handleCloseDeleteModalTopping = () => {
    if (!isDeletingTopping) {
      setOpenDeleteModalTopping(false);
      setToppingToDelete(null);
    }
  };

  // Handle status toggle for toppings (isActive)
  const handleStatusToggleTopping = async (
    toppingId: string,
    toppingName: string,
    currentStatus: boolean
  ) => {
    const newStatus = !currentStatus;
    try {
      const payload = {
        id: toppingId,
        isActive: newStatus,
      };

      console.log("📤 Updating yogurt topping status with payload:", payload);

      const response = await updateYogurtTopping(payload).unwrap();

      console.log("✅ Update yogurt topping status response:", response);

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt topping "${toppingName}" has been ${
          newStatus ? "activated" : "deactivated"
        } successfully`;
      showToast(successMessage);
      refetchToppings();
    } catch (error: any) {
      console.error("Error updating yogurt topping status:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to ${
          newStatus ? "activate" : "deactivate"
        } yogurt topping "${toppingName}". Please try again.`;

      showError(errorMessage);
      refetchToppings(); // Revert UI state if update failed
    }
  };

  // Delete confirmation modal state
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [flavorToDelete, setFlavorToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Handle delete button click - opens confirmation modal
  const handleDeleteClick = (id: string, name: string) => {
    setFlavorToDelete({ id, name });
    setOpenDeleteModal(true);
  };

  // Handle delete confirmation - actually deletes the flavor
  const handleConfirmDelete = async () => {
    if (!flavorToDelete) return;

    try {
      const response = await deleteYogurtFlavor({
        id: flavorToDelete.id,
      }).unwrap();

      // Show success message from API response or default message
      const successMessage =
        response?.message ||
        `Yogurt flavor "${flavorToDelete.name}" has been deleted successfully`;
      showToast(successMessage);

      // Refetch the list after successful deletion
      refetch();
      // Close modal and reset state
      setOpenDeleteModal(false);
      setFlavorToDelete(null);
    } catch (error: any) {
      console.error("Error deleting yogurt flavor:", error);

      // Extract error message from API response or show default
      const errorMessage =
        error?.data?.message ||
        error?.error?.data?.message ||
        error?.data?.error ||
        error?.message ||
        `Failed to delete yogurt flavor "${flavorToDelete.name}". Please try again.`;

      showError(errorMessage);
    }
  };

  // Handle delete modal close
  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setOpenDeleteModal(false);
      setFlavorToDelete(null);
    }
  };

  // Extract yogurt flavors from API response
  const rows = useMemo(() => {
    if (data?.data?.yogurtFlavors && Array.isArray(data.data.yogurtFlavors)) {
      return data.data.yogurtFlavors.map((flavor: any) => ({
        id: flavor._id || flavor.id,
        name: flavor.flavourDetails?.name || "N/A",
        image: flavor.image || "/static/images/placeholder.png",
        pricePer100g: flavor.pricePer100g || 0,
        smallSize: flavor.smallSize || 0,
        mediumSize: flavor.mediumSize || 0,
        largeSize: flavor.largeSize || 0,
        isActive: flavor.isActive ?? true,
        featured: flavor.featured ?? false,
        createdAt: flavor.createdAt
          ? new Date(flavor.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      }));
    }
    return [];
  }, [data]);

  // Calculate total pages
  const totalCount = data?.data?.total || 0;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle page change
  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Extract yogurt toppings from API response
  const rows1 = useMemo(() => {
    if (
      toppingsData?.data?.yogurtToppings &&
      Array.isArray(toppingsData.data.yogurtToppings)
    ) {
      return toppingsData.data.yogurtToppings.map((topping: any) => ({
        id: topping._id || topping.id,
        name: topping.toppingDetails?.name || "N/A",
        price: topping.pricing || topping.price || 0, // Use 'pricing' from API response
        isActive: topping.isActive ?? true,
        toppingId: topping.toppingId || topping.toppingDetails?._id,
        createdAt: topping.createdAt
          ? new Date(topping.createdAt).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "short",
              year: "numeric",
            })
          : "N/A",
      }));
    }
    return [];
  }, [toppingsData]);

  // Calculate total pages for toppings
  const totalCountTopping = toppingsData?.data?.total || 0;
  const totalPagesTopping = Math.ceil(totalCountTopping / limitTopping);

  // Handle page change for toppings
  const handlePageChangeTopping = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPagesTopping) {
      setPageTopping(newPage);
    }
  };

  return (
    <>
      <Loader
        isLoad={
          isLoading ||
          isDeleting ||
          isUpdatingStatus ||
          isLoadingToppings ||
          isDeletingTopping ||
          isUpdatingToppingStatus
        }
      />
      <div className="main_title">
        <h1>Frozen Greek Yogurt</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -{" "}
          <button onClick={() => navigate("/manage-yogurt-types")}>
            Yogurt Types
          </button>{" "}
          - Detail
        </p>
      </div>

      {/* Tabs */}
      <Box className="custom_tabs1">
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
          className="custom_tabs_links"
        >
          <Tab label="Flavors Management" {...a11yProps(0)} />
          <Tab label="Toppings Management" {...a11yProps(1)} />
        </Tabs>
      </Box>

      {/* Table */}
      <div className="cards mt_20">
        <CustomTabPanel value={value} index={0}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTerm}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTerm(e.target.value)
                }
                onCross={() => setSearchTerm("")}
                searchTerm={searchTerm}
                setDebouncedSearchTerm={setSearchTerm}
                placeholder="Search..."
              />
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setEditFlavor(null);
                  setOpenModal(true);
                }}
              >
                <AddIcon /> Add Flavor
              </Button>
            </div>
          </div>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>
                    {pricingMethod === PricingMethod.PER_100G
                      ? " per 100g"
                      : "Price (S/M/L)"}
                  </TableCell>
                  <TableCell>Featured</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {error ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      Error loading yogurt flavors. Please try again.
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      {isLoading ? "Loading..." : "No yogurt flavors found"}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>
                        <Box component="a" className="user_block">
                          <figure>
                            <img
                              src={
                                row.image.startsWith("http")
                                  ? row.image
                                  : `/static/images/${row.image}`
                              }
                              alt={row.name}
                              onError={(e) => {
                                (e.target as HTMLImageElement).src =
                                  "/static/images/placeholder.png";
                              }}
                            />
                          </figure>
                          <p>
                            <b>{row.name}</b>
                          </p>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {pricingMethod === PricingMethod.PER_100G ? (
                          row.pricePer100g
                            ? row.pricePer100g.toFixed(2)
                            : "0.00"
                        ) : (
                          <div>
                            S: {row.smallSize ? row.smallSize.toFixed(2) : "0.00"} / 
                            M: {row.mediumSize ? row.mediumSize.toFixed(2) : "0.00"} / 
                            L: {row.largeSize ? row.largeSize.toFixed(2) : "0.00"}
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <SwitchToggle
                          checked={row.featured}
                          onChange={() =>
                            row.id &&
                            handleFeaturedToggle(
                              row.id,
                              row.name,
                              row.isActive,
                              row.featured
                            )
                          }
                          disabled={isUpdatingStatus || !row.id}
                        />
                      </TableCell>
                      <TableCell>
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={() =>
                            row.id &&
                            handleStatusToggle(
                              row.id,
                              row.name,
                              row.isActive,
                              row.featured
                            )
                          }
                          disabled={isUpdatingStatus || !row.id}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton onClick={() => handleEditClick(row)}>
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(row.id, row.name)}
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 0 && (
            <div
              className="table_footer"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px 20px",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <p style={{ margin: 0, color: "#717171", fontSize: "14px" }}>
                Showing <strong>{(page - 1) * limit + 1}</strong> to{" "}
                <strong>{Math.min(page * limit, totalCount)}</strong> of{" "}
                <strong>{totalCount}</strong> entries
              </p>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Pagination
                  module={rows}
                  page={page}
                  onPageChange={handlePageChange}
                  totalPages={totalPages}
                  setPage={setPage}
                  totalCount={totalCount}
                  limit={limit}
                />
              </div>
            </div>
          )}
        </CustomTabPanel>

        <CustomTabPanel value={value} index={1}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                value={searchTermTopping}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchTermTopping(e.target.value)
                }
                onCross={() => setSearchTermTopping("")}
                searchTerm={searchTermTopping}
                setDebouncedSearchTerm={setSearchTermTopping}
                placeholder="Search..."
              />
            </div>
            <div className="right_s">
              <Button
                className="btn btn_primary"
                onClick={() => {
                  setEditTopping(null);
                  setOpenModal1(true);
                }}
              >
                <AddIcon /> Add Toppings
              </Button>
            </div>
          </div>

          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Price (OMR)</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoadingToppings ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : errorToppings ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Error loading toppings. Please try again.
                    </TableCell>
                  </TableRow>
                ) : rows1.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      No yogurt toppings found
                    </TableCell>
                  </TableRow>
                ) : (
                  rows1.map((row, index) => (
                    <TableRow hover key={row.id}>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>
                        {row.price ? row.price.toFixed(2) : "0.00"}
                      </TableCell>
                      <TableCell>
                        <SwitchToggle
                          checked={row.isActive}
                          onChange={() =>
                            row.id &&
                            handleStatusToggleTopping(
                              row.id,
                              row.name,
                              row.isActive
                            )
                          }
                          disabled={isUpdatingToppingStatus || !row.id}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="table_actions v2">
                          <IconButton
                            onClick={() => handleEditClickTopping(row)}
                          >
                            <img
                              src="/static/images/edit2_icon.svg"
                              alt="Edit"
                            />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              handleDeleteClickTopping(row.id, row.name)
                            }
                          >
                            <img
                              src="/static/images/trash2_icon.svg"
                              alt="Delete"
                            />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPagesTopping > 0 && (
            <div
              className="table_footer"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "15px 20px",
                borderTop: "1px solid #e0e0e0",
              }}
            >
              <p style={{ margin: 0, color: "#717171", fontSize: "14px" }}>
                Showing <strong>{(pageTopping - 1) * limitTopping + 1}</strong>{" "}
                to{" "}
                <strong>
                  {Math.min(pageTopping * limitTopping, totalCountTopping)}
                </strong>{" "}
                of <strong>{totalCountTopping}</strong> entries
              </p>
              <div
                style={{ display: "flex", alignItems: "center", gap: "10px" }}
              >
                <Pagination
                  module={rows1}
                  page={pageTopping}
                  onPageChange={handlePageChangeTopping}
                  totalPages={totalPagesTopping}
                  setPage={setPageTopping}
                  totalCount={totalCountTopping}
                  limit={limitTopping}
                />
              </div>
            </div>
          )}
        </CustomTabPanel>
      </div>

      <AddTypeFlavor
        open={openModal}
        onClose={handleCloseModal}
        setOpen={setOpenModal}
        yogurtId={id || ""}
        editFlavor={editFlavor}
        pricingMethod={pricingMethod}
      />

      <AddTypeTopping
        open={openModal1}
        onClose={handleCloseModal1}
        setOpen={setOpenModal1}
        yogurtId={id || ""}
        editTopping={editTopping}
      />

      <DeleteConfirmationModal
        open={openDeleteModal}
        onClose={handleCloseDeleteModal}
        setOpen={setOpenDeleteModal}
        onConfirm={handleConfirmDelete}
        itemName={flavorToDelete?.name || ""}
        isLoading={isDeleting}
      />

      <DeleteConfirmationModal
        open={openDeleteModalTopping}
        onClose={handleCloseDeleteModalTopping}
        setOpen={setOpenDeleteModalTopping}
        onConfirm={handleConfirmDeleteTopping}
        itemName={toppingToDelete?.name || ""}
        isLoading={isDeletingTopping}
      />
    </>
  );
};

export default YogurtTypeDetail;
